import { useTranslation } from 'react-i18next';
import React, { useContext, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CircularProgress, Grid } from '@mui/material';
import { GrievanceLogbookItem } from '../../../../services/types/Grievance';
import { Cell, UIKitCheckbox } from '../../../../shared/UIKITComponents';
import { customDateStr } from '../../../../shared/misc/utils';
import UIKitTable from '../../../../shared/UIKITComponents/Table';
import UIKitIconButton from '../../../../shared/UIKITComponents/IconButton';
import UIKitLink from '../../../../shared/UIKITComponents/Link';
import UIKitConfirmModal from '../../../../shared/UIKITComponents/ConfirmModal';
import UIKitModal from '../../../../shared/UIKITComponents/Modal';
import UIKitButton from '../../../../shared/UIKITComponents/Button';
import UIKitDropDown from '../../../../shared/UIKITComponents/DropDown';
import { OptionValue } from '../../../../services/types/Misc';
import UIKitInput from '../../../../shared/UIKITComponents/Input';
import MainContext from '../../../App/MainProvider/context';
import grievanceService from '../../../../services/grievanceService';
import Context from '../context/DetailsContext';

interface GrievanceLogbookItemEditProps {
  logbookItem: GrievanceLogbookItem;
  onSave: (logbookItem: GrievanceLogbookItem, messageSuccess?: string) => void;
  onClose: () => void;
}

const GrievanceLogbookItemEdit: React.FC<GrievanceLogbookItemEditProps> = (props) => {
  const { logbookItem, onSave, onClose } = props;
  const { t } = useTranslation();
  const { phases, lookupsDocumentTypes } = useContext(Context);

  const modalTitle = logbookItem.documentTypeId ? t('editAttachments') : t('editComment');
  const messageSuccess = logbookItem.documentTypeId
    ? t('attachmentUpdatedSuccessfully')
    : t('commentUpdatedSuccessfully');

  const formik = useFormik<GrievanceLogbookItem>({
    initialValues: {
      ...logbookItem,
    },
    validationSchema: Yup.object({
      phaseId: Yup.number().required(t('required')),
      documentTypeId: Yup.number().nullable(),
      comments: Yup.string()
        .required(t('required'))
        .max(750, t('maxLengthCharacters', { length: 750 })),
    }),
    onSubmit: (values) => {
      onSave(values, messageSuccess);
      onClose();
    },
  });

  const currentPhaseIndex = phases.findIndex((phase) => phase.isCurrent);

  const lookupsPhases = phases.map((phase, i) => ({
    text: t(`grievanceStages.${phase.phase}`),
    id: phase.phaseId,
    disabled: i > currentPhaseIndex,
  }));

  const documentTypeOptions: OptionValue[] = useMemo(
    () => lookupsDocumentTypes.map((v) => ({ text: v.name, id: v.id })),
    [lookupsDocumentTypes]
  );

  return (
    <UIKitModal
      title={modalTitle}
      open
      onClose={onClose}
      size="md"
      actions={
        <>
          <UIKitButton text={t('cancel')} variant="link" onClick={onClose} color="secondary" />
          <UIKitButton
            text={t('save')}
            variant="flat"
            color="primary"
            disabled={!formik.isValid}
            onClick={formik.submitForm}
          />
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {logbookItem.fromTemplate && (
            <Grid size={12}>
              <span className="document-from-template">{t('documentFromTemplate')}</span>
            </Grid>
          )}
          {logbookItem.documentTypeId && (
            <Grid size={6}>
              <UIKitDropDown
                elements={documentTypeOptions}
                value={formik.values.documentTypeId || null}
                onChange={(value) => formik.setFieldValue('documentTypeId', value)}
                label={t('documentType')}
                error={formik.touched.documentTypeId && !!formik.errors.documentTypeId}
                helperText={formik.errors.documentTypeId as string}
                fullWidth
                disabled={logbookItem.fromTemplate}
              />
            </Grid>
          )}
          <Grid size={6}>
            <UIKitDropDown
              label={t('selectPhase')}
              elements={lookupsPhases}
              value={formik.values.phaseId}
              onChange={(value) => formik.setFieldValue('phaseId', value)}
              error={formik.touched.phaseId && !!formik.errors.phaseId}
              helperText={formik.errors.phaseId as string}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <UIKitInput
              multiline
              rows={4}
              label={t('comment')}
              name="comments"
              placeholder={t('typeYourComment')}
              value={formik.values.comments}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.comments && !!formik.errors.comments}
              helperText={formik.touched.comments && formik.errors.comments}
              fullWidth
            />
          </Grid>
        </Grid>
      </form>
    </UIKitModal>
  );
};

interface GrievanceLogbookItemProps {
  grievanceNumber: string;
  logbookItems?: GrievanceLogbookItem[];
  onRemoveComment: (commentId: number, messageSuccess?: string) => void;
  onSaveLogbookItem: (logbookItem: GrievanceLogbookItem, messageSuccess?: string) => void;
  onSaveIsFinalFlagCallback: () => void;
}

const LogbookItemsGrid: React.FC<GrievanceLogbookItemProps> = (props) => {
  const {
    grievanceNumber,
    logbookItems,
    onRemoveComment,
    onSaveLogbookItem,
    onSaveIsFinalFlagCallback,
  } = props;
  const { service } = useContext(MainContext);

  const { t } = useTranslation();

  const [downloadingDocument] = useState<boolean>(false);
  const [documentToDownload] = useState<GrievanceLogbookItem | null>(null);

  const [selectedLogbookItemForDeletion, setSelectedLogbookItemForDeletion] =
    useState<GrievanceLogbookItem | null>(null);

  const [selectedLogbookItemForEdition, setSelectedLogbookItemForEdition] =
    useState<GrievanceLogbookItem | null>(null);

  const [savingIsFinalFlag, setSavingIsFinalFlag] = useState<boolean>(false);

  const [itemForIsFinalFlag, setItemForIsFinalFlag] = useState<{
    id: number;
    isFinal: boolean;
  } | null>();
  const [showConfirmSetIsFinalFlag, setShowConfirmSetIsFinalFlag] = useState(false);

  if (!logbookItems || logbookItems.length === 0) {
    return null;
  }

  const onSaveIsFinalFlag = () => {
    service(
      () =>
        grievanceService.saveIsFinalFlag(
          grievanceNumber,
          itemForIsFinalFlag!.id,
          itemForIsFinalFlag!.isFinal
        ),
      {
        loader: setSavingIsFinalFlag,
        callback: () => {
          onSaveIsFinalFlagCallback();
          setItemForIsFinalFlag(null);
        },
      }
    );
  };

  const cells: Cell<GrievanceLogbookItem>[] = useMemo(
    () => [
      {
        name: 'createdBy',
        value: (x) => x.createdBy,
        column: t('createdBy'),
      },
      {
        name: 'type',
        value: (x) => x.type,
        column: t('type'),
      },
      {
        name: 'createdDate',
        value: (x) => customDateStr(x.createdDate),
        column: t('date'),
      },
      {
        name: 'comments',
        value: (x) => x.comments,
        column: t('comments'),
      },
      {
        name: 'isFinal',
        value: (x) => {
          if (x.approveRequired) {
            return '';
          }
          return x.isFinal ? 'Yes' : 'No';
        },
        width: 100,
        column: t('finalVersion'),
        render: (value, row) => {
          if (!row.approveRequired) {
            return <></>;
          }

          if (savingIsFinalFlag && itemForIsFinalFlag && itemForIsFinalFlag.id === row.id) {
            return <CircularProgress size={18} thickness={4} style={{ marginLeft: '10px' }} />;
          }

          return (
            <UIKitCheckbox
              checked={row.isFinal}
              disabled={!row.canSetIsFinal}
              onChange={(e) => {
                setItemForIsFinalFlag({ id: row.id, isFinal: e.target.checked });
                setShowConfirmSetIsFinalFlag(true);
              }}
            />
          );
        },
      },
      {
        name: 'documentLink',
        column: t('attachment'),
        width: 300,
        render: (v, row) => {
          if (row.documentLink) {
            return <UIKitLink text={row.documentName} href={row.documentLink} target="_blank" />;
          }

          return <></>;
        },
      },
      {
        column: 'actions',
        width: 120,
        render: (value, row) => (
          <div className="actions">
            {row.canEdit && (
              <UIKitIconButton
                iconName="edit"
                onClick={() => {
                  setSelectedLogbookItemForEdition(row);
                }}
              />
            )}
            {row.canDelete && (
              <UIKitIconButton
                iconName="delete"
                onClick={() => {
                  setSelectedLogbookItemForDeletion(row);
                }}
              />
            )}
          </div>
        ),
      },
    ],
    [savingIsFinalFlag, itemForIsFinalFlag, logbookItems, downloadingDocument, documentToDownload]
  );

  return (
    <div className="logbook-items-grid">
      <UIKitTable<GrievanceLogbookItem>
        cells={cells}
        data={logbookItems}
        keyFunc={(x) => x.id}
        headerContrast="low"
        emptyNoDesc
      />

      <UIKitConfirmModal
        open={selectedLogbookItemForDeletion !== null}
        onClose={() => setSelectedLogbookItemForDeletion(null)}
        onConfirm={() => {
          const messageSuccess =
            selectedLogbookItemForDeletion !== null && selectedLogbookItemForDeletion.documentTypeId
              ? t('documentRemovedSuccessfully')
              : t('commentRemovedSuccessfully');

          onRemoveComment(selectedLogbookItemForDeletion!.id, messageSuccess);
          setSelectedLogbookItemForDeletion(null);
        }}
        title={t('confirmDeleteLogbookItem')}
        message={
          selectedLogbookItemForDeletion !== null && selectedLogbookItemForDeletion.documentTypeId
            ? t('confirmDeleteDocument')
            : t('confirmDeleteComment')
        }
        submitText={t('yes')}
        cancelText={t('no')}
      />

      {itemForIsFinalFlag && (
        <UIKitConfirmModal
          open={showConfirmSetIsFinalFlag}
          onClose={() => setShowConfirmSetIsFinalFlag(false)}
          onConfirm={() => onSaveIsFinalFlag()}
          title={t('confirm')}
          message={
            itemForIsFinalFlag.isFinal
              ? t('confirmSetIsFinalFlagMessage')
              : t('confirmUnsetIsFinalFlagMessage')
          }
          submitText={t('yes')}
          cancelText={t('no')}
        />
      )}

      {selectedLogbookItemForEdition !== null && (
        <GrievanceLogbookItemEdit
          logbookItem={selectedLogbookItemForEdition}
          onSave={onSaveLogbookItem}
          onClose={() => setSelectedLogbookItemForEdition(null)}
        />
      )}
    </div>
  );
};

export default LogbookItemsGrid;
